import React from 'react'
import { Button } from '../ui/Button'
import { useI18n } from '../../i18n'
import { READINESS_OPTIONS } from '../assets/informationSystemGrcConstants'
import CorrelationsPanel from '../assets/CorrelationsPanel'
import OperationalDashboardPanel from '../assets/OperationalDashboardPanel'
import BlastRadiusPanel from '../assets/BlastRadiusPanel'
import RiskComplianceDetailPanel, { RiskComplianceDetailEmpty } from '../assets/RiskComplianceDetailPanel'
import type { GrcSummaryRow } from '../../services/informationSystemComplianceApi'
import {
  fetchRiskComplianceExecutiveSummary,
  type InformationSystemExecutiveSummary,
  type RiskComplianceExecutiveSummary,
} from '../../services/enterpriseOperationalUxApi'
import RiskComplianceExecutiveRow from './RiskComplianceExecutiveRow'
import RiskComplianceExecutiveContextPanel, { type RiskComplianceSelection } from './RiskComplianceExecutiveContextPanel'
import RiskComplianceRiskMiniGraph from './RiskComplianceRiskMiniGraph'
import entStyles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'
import styles from '../../styles/components/assets/AssetsRiskCompliance.module.scss'

const LIFECYCLE_OPTIONS = ['DRAFT', 'ACTIVE', 'IN_REVIEW', 'DEPRECATED', 'ARCHIVED']
const IMPLEMENTATION_OPTIONS = ['NOT_STARTED', 'PLANNED', 'IMPLEMENTED', 'PARTIAL', 'NOT_APPLICABLE', 'NEEDS_REVIEW']

type Props = {
  summaries: GrcSummaryRow[]
  loading: boolean
  error: string | null
  searchQuery: string
  setSearchQuery: (v: string) => void
  filterLifecycle: string
  setFilterLifecycle: (v: string) => void
  filterStandard: string
  setFilterStandard: (v: string) => void
  filterReadiness: string
  setFilterReadiness: (v: string) => void
  filterImplementation: string
  setFilterImplementation: (v: string) => void
  standardOptions: string[]
  onRefresh: () => void
  onOpenSystem?: (systemId: string) => void
  onOpenGraph?: (systemId: string) => void
}

export default function RiskComplianceExecutiveWorkspace({
  summaries,
  loading,
  error,
  searchQuery,
  setSearchQuery,
  filterLifecycle,
  setFilterLifecycle,
  filterStandard,
  setFilterStandard,
  filterReadiness,
  setFilterReadiness,
  filterImplementation,
  setFilterImplementation,
  standardOptions,
  onRefresh,
  onOpenSystem,
  onOpenGraph,
}: Props) {
  const { t } = useI18n()
  const [summary, setSummary] = React.useState<RiskComplianceExecutiveSummary | null>(null)
  const [summaryLoading, setSummaryLoading] = React.useState(true)
  const [intelligenceOpen, setIntelligenceOpen] = React.useState(false)
  const [graphOpen, setGraphOpen] = React.useState(true)
  const [selectedId, setSelectedId] = React.useState<string | null>(null)
  const [selection, setSelection] = React.useState<RiskComplianceSelection>(null)

  const loadSummary = React.useCallback(async () => {
    setSummaryLoading(true)
    try {
      const codes = filterStandard ? [filterStandard] : standardOptions
      const data = await fetchRiskComplianceExecutiveSummary(codes)
      setSummary(data?.enterpriseUxEnabled !== false ? data : null)
    } catch {
      setSummary(null)
    } finally {
      setSummaryLoading(false)
    }
  }, [filterStandard, standardOptions.join(',')])

  React.useEffect(() => {
    void loadSummary()
  }, [loadSummary])

  const executiveBySystemId = React.useMemo(() => {
    const map: Record<string, InformationSystemExecutiveSummary> = {}
    for (const s of summary?.systems || []) {
      map[s.informationSystemId] = s
    }
    return map
  }, [summary?.systems])

  const filteredGrc = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return summaries
    return summaries.filter((row) => row.name.toLowerCase().includes(q))
  }, [summaries, searchQuery])

  const selectedRow = React.useMemo(
    () => (selectedId ? summaries.find((r) => r.informationSystemId === selectedId) : null),
    [summaries, selectedId],
  )

  const standardRows = React.useMemo(() => {
    const rows = summary?.standardRows || []
    if (!filterStandard) return rows
    return rows.filter((r) => r.standardCode === filterStandard)
  }, [summary?.standardRows, filterStandard])

  const posture = summary?.postureStrip

  return (
    <div className={styles.riskComplianceWorkspace} data-testid="risk-compliance-executive-workspace">
      <section className={entStyles.layer}>
        <p className={entStyles.layerLabel}>
          {t('enterpriseOperationalUx.riskCompliance.layerPosture', 'Executive risk posture')}
        </p>
        <div className={entStyles.posturePanel}>
          <h2 className={entStyles.postureTitle}>
            {t('enterpriseOperationalUx.riskCompliance.commandCenter', 'Operational Risk & Compliance Command Center')}
          </h2>
          <p className={entStyles.postureSubtitle}>
            {t(
              'enterpriseOperationalUx.riskCompliance.workspaceHint',
              'Which standards are unstable, which workflows are degrading, and where shared controls create certification risk.',
            )}
          </p>
          {summaryLoading ? <p className={entStyles.emptyState}>{t('common.loading', 'Loading…')}</p> : null}
          {posture ? (
            <div className={entStyles.postureGrid}>
              <div className={entStyles.postureMetric}>
                <p className={entStyles.postureMetricLabel}>
                  {t('enterpriseOperationalUx.riskCompliance.certificationInstability', 'Certification instability')}
                </p>
                <p className={entStyles.postureMetricValue}>{posture.certificationRisk || '—'}</p>
              </div>
              <div className={entStyles.postureMetric}>
                <p className={entStyles.postureMetricLabel}>
                  {t('enterpriseOperationalUx.riskCompliance.workflowRegression', 'Workflow regression')}
                </p>
                <p className={entStyles.postureMetricValue}>{posture.degradedWorkflows}</p>
              </div>
              <div className={entStyles.postureMetric}>
                <p className={entStyles.postureMetricLabel}>
                  {t('enterpriseOperationalUx.riskCompliance.unstableControls', 'Unstable controls')}
                </p>
                <p className={entStyles.postureMetricValue}>{posture.unstableControls}</p>
              </div>
              <div className={entStyles.postureMetric}>
                <p className={entStyles.postureMetricLabel}>{t('executiveGovernance.blockers', 'Blockers')}</p>
                <p className={entStyles.postureMetricValue}>{posture.blockerConcentration}</p>
              </div>
              <div className={entStyles.postureMetric}>
                <p className={entStyles.postureMetricLabel}>{t('enterpriseOperationalUx.staleEvidence', 'Stale evidence')}</p>
                <p className={entStyles.postureMetricValue}>{posture.staleEvidencePressure}</p>
              </div>
              <div className={entStyles.postureMetric}>
                <p className={entStyles.postureMetricLabel}>
                  {t('enterpriseOperationalUx.riskCompliance.runtimeDegradation', 'Runtime degradation')}
                </p>
                <p className={entStyles.postureMetricValue}>{posture.runtimeDegradationCount}</p>
              </div>
              <div className={entStyles.postureMetric}>
                <p className={entStyles.postureMetricLabel}>
                  {t('enterpriseOperationalUx.riskCompliance.blastRadiusConcentration', 'Blast radius concentration')}
                </p>
                <p className={entStyles.postureMetricValue}>{posture.blastRadiusConcentration}</p>
              </div>
              <div className={entStyles.postureMetric}>
                <p className={entStyles.postureMetricLabel}>
                  {t('enterpriseOperationalUx.dataMap.highestPressure', 'Highest pressure')}
                </p>
                <p className={entStyles.postureMetricValue}>{posture.highPressureSystems}</p>
              </div>
            </div>
          ) : null}
          {summary?.explanations?.length ? (
            <p className={entStyles.postureStory}>{summary.explanations.join(' ')}</p>
          ) : null}
        </div>
      </section>

      <section className={styles.rcSignalsRow}>
        <div className={styles.rcSignalBlock}>
          <h3 className={styles.rcSignalTitle}>{t('enterpriseOperationalUx.layerTimeline', 'Runtime operational timeline')}</h3>
          {summary?.runtimeEvents?.length ? (
            <ul className={styles.rcSignalList}>
              {summary.runtimeEvents.slice(0, 8).map((ev) => (
                <li key={ev.id}>
                  <button type="button" className={styles.rcSignalBtn} onClick={() => setSelection({ kind: 'runtime', event: ev })}>
                    <span className={styles.rcRuntimeDot} aria-hidden />
                    {ev.eventType} — {ev.summary}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className={styles.widgetEmpty}>
              {t('enterpriseOperationalUx.riskCompliance.noRuntimeDegradation', 'No operational degradation detected')}
            </p>
          )}
        </div>
        <div className={styles.rcSignalBlock}>
          <h3 className={styles.rcSignalTitle}>{t('enterpriseOperationalUx.layerBlast', 'Shared control blast radius')}</h3>
          {summary?.blastRadiusImpacts?.length ? (
            <ul className={styles.rcSignalList}>
              {summary.blastRadiusImpacts.slice(0, 6).map((imp) => (
                <li key={imp.controlCode}>
                  <button type="button" className={styles.rcSignalBtn} onClick={() => setSelection({ kind: 'blast', impact: imp })}>
                    {imp.controlCode} — {imp.summary}
                    {(imp.standardCount || 0) >= 2 ? (
                      <span className={styles.rcBlastMeta}>
                        {' '}
                        · {imp.standardCount} {t('enterpriseOperationalUx.standardsImpacted', 'standards')} · {imp.systemCount}{' '}
                        {t('enterpriseOperationalUx.systemsImpacted', 'systems')}
                      </span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className={styles.widgetEmpty}>
              {t('enterpriseOperationalUx.riskCompliance.noBlastPropagation', 'No blast radius propagation')}
            </p>
          )}
        </div>
      </section>

      <section className={styles.intelligenceSection}>
        <div className={styles.intelligenceSectionHeader}>
          <h2 className={styles.intelligenceSectionTitle}>
            {t('assets.riskCompliance.intelligenceTitle', 'Org intelligence')}
          </h2>
          <button type="button" className={styles.collapseBtn} onClick={() => setIntelligenceOpen((v) => !v)} aria-expanded={intelligenceOpen}>
            {intelligenceOpen ? t('common.hide', 'Hide') : t('common.show', 'Show')}
          </button>
        </div>
        {intelligenceOpen ? (
          <div className={styles.intelligenceGrid}>
            <OperationalDashboardPanel />
            <CorrelationsPanel />
            <BlastRadiusPanel />
          </div>
        ) : null}
      </section>

      <div className={styles.rcToolbar}>
        <div className={styles.toolbarLeft}>
          <div className={styles.toolbarPrimaryRow}>
            <input
              type="search"
              className={styles.searchField}
              placeholder={t('assets.riskCompliance.searchPlaceholder', 'Search systems…')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className={styles.filterFieldWrap}>
              <span className={styles.filterLabel}>{t('assets.lifecycle.status', 'Lifecycle')}</span>
              <select className={styles.selectField} value={filterLifecycle} onChange={(e) => setFilterLifecycle(e.target.value)}>
                <option value="">{t('assets.lifecycle.allStatuses', 'All statuses')}</option>
                {LIFECYCLE_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {t(`assets.lifecycle.${s}`, s)}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.filterFieldWrap}>
              <span className={styles.filterLabel}>{t('assets.grc.standardCode', 'Standard')}</span>
              <select className={styles.selectField} value={filterStandard} onChange={(e) => setFilterStandard(e.target.value)}>
                <option value="">{t('assets.grc.allStandards', 'All standards')}</option>
                {standardOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.filterFieldWrap}>
              <span className={styles.filterLabel}>{t('assets.grc.readinessStatus', 'Readiness')}</span>
              <select className={styles.selectField} value={filterReadiness} onChange={(e) => setFilterReadiness(e.target.value)}>
                <option value="">{t('assets.grc.allReadiness', 'All readiness')}</option>
                {READINESS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {t(`assets.grc.readiness.${s}`, s)}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.filterFieldWrap}>
              <span className={styles.filterLabel}>{t('assets.grc.implementationFilter', 'Control status')}</span>
              <select className={styles.selectField} value={filterImplementation} onChange={(e) => setFilterImplementation(e.target.value)}>
                <option value="">{t('assets.grc.allImplementation', 'Any status')}</option>
                {IMPLEMENTATION_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {t(`assets.grc.implementation.${s}`, s)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className={styles.toolbarRight}>
          <button
            type="button"
            className={styles.refreshBtn}
            onClick={() => {
              onRefresh()
              void loadSummary()
            }}
          >
            {t('common.refresh', 'Refresh')}
          </button>
        </div>
      </div>

      {error ? (
        <p className={styles.errorBanner}>
          {error}
          <Button type="button" size="small" color="outline" onClick={onRefresh}>
            {t('common.retry', 'Retry')}
          </Button>
        </p>
      ) : null}

      <section className={styles.rcExecutiveWorkspace}>
        <div className={styles.rcExecutiveMain}>
          <h3 className={styles.rcSectionTitle}>
            {t('enterpriseOperationalUx.riskCompliance.degradedStandards', 'Degraded standards')}
          </h3>
          {summaryLoading ? (
            <p className={styles.loadingText}>{t('common.loading')}</p>
          ) : standardRows.length === 0 ? (
            <p className={styles.widgetEmpty}>
              {t('enterpriseOperationalUx.riskCompliance.noCertificationInstability', 'No certification instability')}
            </p>
          ) : (
            <div className={styles.rcExecutiveList}>
              {standardRows.map((row) => (
                <RiskComplianceExecutiveRow
                  key={row.standardCode}
                  kind="standard"
                  row={row}
                  selected={selection?.kind === 'standard' && selection.row.standardCode === row.standardCode}
                  onSelect={() => setSelection({ kind: 'standard', row })}
                />
              ))}
            </div>
          )}

          {summary?.unstableWorkflows?.length ? (
            <>
              <h3 className={styles.rcSectionTitle} style={{ marginTop: '1rem' }}>
                {t('enterpriseOperationalUx.riskCompliance.unstableWorkflows', 'Unstable workflows')}
              </h3>
              <ul className={styles.rcSignalList}>
                {summary.unstableWorkflows.slice(0, 12).map((w, i) => (
                  <li key={`${w.informationSystemId}-${w.standardCode}-${i}`}>
                    <button type="button" className={styles.rcSignalBtn} onClick={() => setSelection({ kind: 'workflow', workflow: w })}>
                      {w.standardCode} — {w.workflowStage}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className={styles.widgetEmpty} style={{ marginTop: '0.5rem' }}>
              {t('enterpriseOperationalUx.riskCompliance.noWorkflowRegressions', 'No workflow regressions')}
            </p>
          )}

          <h3 className={styles.rcSectionTitle} style={{ marginTop: '1rem' }}>
            {t('enterpriseOperationalUx.riskCompliance.unstableSystems', 'Unstable systems')}
          </h3>
          {loading ? (
            <p className={styles.loadingText}>{t('common.loading')}</p>
          ) : filteredGrc.length === 0 ? (
            <p className={styles.widgetEmpty}>{t('assets.riskCompliance.noSearchResults', 'No systems match your filters.')}</p>
          ) : (
            <div className={styles.rcExecutiveList}>
              {filteredGrc.map((row) => (
                <RiskComplianceExecutiveRow
                  key={row.informationSystemId}
                  kind="system"
                  grc={row}
                  executive={executiveBySystemId[row.informationSystemId]}
                  selected={selectedId === row.informationSystemId}
                  onSelect={() => {
                    setSelectedId(row.informationSystemId)
                    setSelection({ kind: 'system', systemId: row.informationSystemId, grc: row })
                  }}
                  onOpenPassport={onOpenSystem ? () => onOpenSystem(row.informationSystemId) : undefined}
                  onOpenGraph={onOpenGraph ? () => onOpenGraph(row.informationSystemId) : undefined}
                />
              ))}
            </div>
          )}

          <section style={{ marginTop: '1rem' }}>
            <div className={styles.intelligenceSectionHeader}>
              <h3 className={styles.rcSectionTitle}>
                {t('enterpriseOperationalUx.riskCompliance.riskGraph', 'Operational risk graph')}
              </h3>
              <button type="button" className={styles.collapseBtn} onClick={() => setGraphOpen((v) => !v)}>
                {graphOpen ? t('common.hide', 'Hide') : t('common.show', 'Show')}
              </button>
            </div>
            {graphOpen && summary ? (
              <RiskComplianceRiskMiniGraph standardRows={summary.standardRows} blastImpacts={summary.blastRadiusImpacts} />
            ) : null}
          </section>
        </div>

        <aside className={styles.rcExecutiveAside}>
          <RiskComplianceExecutiveContextPanel
            selection={selection}
            executiveBySystemId={executiveBySystemId}
            onClose={() => setSelection(null)}
          />
          {selectedRow ? (
            <div className={styles.rcDetailStack}>
              <RiskComplianceDetailPanel
                row={selectedRow}
                onClose={() => {
                  setSelectedId(null)
                  if (selection?.kind === 'system') setSelection(null)
                }}
                onOpenPassport={onOpenSystem ? () => onOpenSystem(selectedRow.informationSystemId) : undefined}
                onOpenGraph={onOpenGraph ? () => onOpenGraph(selectedRow.informationSystemId) : undefined}
              />
            </div>
          ) : (
            <RiskComplianceDetailEmpty />
          )}
        </aside>
      </section>
    </div>
  )
}
